import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { settingsAPI, STORAGE_URL } from '../services/api';
import toast from 'react-hot-toast';
import { FiUpload, FiTrash2, FiSave, FiImage } from 'react-icons/fi';
import ConfirmDialog from '../components/common/ConfirmDialog';

const Settings = () => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({
        logo: null,
        favicon: null,
        stamp: null,
        office_name: '',
        office_name_mr: '',
        office_address: '',
        office_address_mr: '',
        office_phone: '',
        office_email: '',
        authority_name: '',
        authority_name_mr: '',
        place: '',
        place_mr: '',
        authority_signature: null,
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [faviconPreview, setFaviconPreview] = useState(null);
    const [stampPreview, setStampPreview] = useState(null);
    const [authoritySignaturePreview, setAuthoritySignaturePreview] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '' });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await settingsAPI.get();
            setSettings(response.data);
            if (response.data.logo) {
                setLogoPreview(`${STORAGE_URL}/${response.data.logo}`);
            }
            if (response.data.favicon) {
                setFaviconPreview(`${STORAGE_URL}/${response.data.favicon}`);
            }
            if (response.data.stamp) {
                setStampPreview(`${STORAGE_URL}/${response.data.stamp}`);
            }
            if (response.data.authority_signature) {
                setAuthoritySignaturePreview(`${STORAGE_URL}/${response.data.authority_signature}`);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('certificate.fileSizeExceed'));
                return;
            }
            setLogoPreview(URL.createObjectURL(file));
            uploadLogo(file);
        }
    };

    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 1 * 1024 * 1024) {
                toast.error(t('settings.faviconRecommendation'));
                return;
            }
            setFaviconPreview(URL.createObjectURL(file));
            uploadFavicon(file);
        }
    };

    const uploadLogo = async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('logo', file);
            const response = await settingsAPI.uploadLogo(formData);
            toast.success(t('settings.logoUploaded'));
            setSettings(prev => ({ ...prev, logo: response.data.path }));
        } catch (error) {
            toast.error(t('settings.failedUploadLogo'));
        } finally {
            setLoading(false);
        }
    };

    const uploadFavicon = async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('favicon', file);
            const response = await settingsAPI.uploadFavicon(formData);
            toast.success(t('settings.faviconUploaded'));
            setSettings(prev => ({ ...prev, favicon: response.data.path }));
        } catch (error) {
            toast.error(t('settings.failedUploadFavicon'));
        } finally {
            setLoading(false);
        }
    };

    const handleStampChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('certificate.fileSizeExceed'));
                return;
            }
            setStampPreview(URL.createObjectURL(file));
            uploadStamp(file);
        }
    };

    const uploadStamp = async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('stamp', file);
            const response = await settingsAPI.uploadStamp(formData);
            toast.success('Stamp uploaded successfully');
            setSettings(prev => ({ ...prev, stamp: response.data.path }));
        } catch (error) {
            toast.error('Failed to upload stamp');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLogo = async () => {
        setConfirmDialog({ isOpen: true, type: 'logo' });
    };

    const handleDeleteFavicon = async () => {
        setConfirmDialog({ isOpen: true, type: 'favicon' });
    };

    const handleDeleteStamp = async () => {
        setConfirmDialog({ isOpen: true, type: 'stamp' });
    };

    const handleAuthoritySignatureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error(t('certificate.fileSizeExceed'));
                return;
            }
            setAuthoritySignaturePreview(URL.createObjectURL(file));
            uploadAuthoritySignature(file);
        }
    };

    const uploadAuthoritySignature = async (file) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('authority_signature', file);
            const response = await settingsAPI.uploadAuthoritySignature(formData);
            toast.success('Authority signature uploaded successfully');
            setSettings(prev => ({ ...prev, authority_signature: response.data.path }));
        } catch (error) {
            toast.error('Failed to upload authority signature');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAuthoritySignature = async () => {
        setConfirmDialog({ isOpen: true, type: 'authority_signature' });
    };

    const handleConfirmDelete = async () => {
        const type = confirmDialog.type;
        setConfirmDialog({ isOpen: false, type: '' });
        try {
            if (type === 'logo') {
                await settingsAPI.deleteLogo();
                setLogoPreview(null);
                setSettings(prev => ({ ...prev, logo: null }));
                toast.success(t('settings.logoDeleted'));
            } else if (type === 'favicon') {
                await settingsAPI.deleteFavicon();
                setFaviconPreview(null);
                setSettings(prev => ({ ...prev, favicon: null }));
                toast.success(t('settings.faviconDeleted'));
            } else if (type === 'stamp') {
                await settingsAPI.deleteStamp();
                setStampPreview(null);
                setSettings(prev => ({ ...prev, stamp: null }));
                toast.success('Stamp deleted successfully');
            } else if (type === 'authority_signature') {
                await settingsAPI.deleteAuthoritySignature();
                setAuthoritySignaturePreview(null);
                setSettings(prev => ({ ...prev, authority_signature: null }));
                toast.success('Authority signature deleted successfully');
            }
        } catch (error) {
            toast.error(type === 'logo' ? t('settings.failedDeleteLogo') : type === 'favicon' ? t('settings.failedDeleteFavicon') : 'Failed to delete stamp');
        }
    };

    const handleOfficeSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await settingsAPI.updateOffice({
                office_name: settings.office_name,
                office_name_mr: settings.office_name_mr,
                office_address: settings.office_address,
                office_address_mr: settings.office_address_mr,
                office_phone: settings.office_phone,
                office_email: settings.office_email,
                authority_name: settings.authority_name,
                authority_name_mr: settings.authority_name_mr,
                place: settings.place,
                place_mr: settings.place_mr,
            });
            toast.success(t('settings.officeSaved'));
        } catch (error) {
            toast.error(t('settings.failedSaveOffice'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">
                {t('settings.title')}
            </h1>

            {/* Logo & Favicon Section */}
            <div className="bg-white shadow rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    {t('settings.logoAndFavicon')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Logo Upload */}
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            {t('settings.logo')}
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {logoPreview ? (
                                <div className="relative">
                                    <img
                                        src={logoPreview}
                                        alt="Logo preview"
                                        className="max-h-32 mx-auto mb-4"
                                    />
                                    <button
                                        onClick={handleDeleteLogo}
                                        className="text-red-600 hover:text-red-800 flex items-center justify-center gap-1 mx-auto"
                                    >
                                        <FiTrash2 /> {t('settings.deleteLogo')}
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                                    <p className="text-gray-500 mb-2">{t('settings.noLogo')}</p>
                                </div>
                            )}
                            <label className="cursor-pointer mt-4">
                                <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <FiUpload className="mr-2" />
                                    {t('settings.uploadLogo')}
                                </span>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml"
                                    onChange={handleLogoChange}
                                    className="hidden"
                                />
                            </label>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('settings.logoRecommendation')}
                        </p>
                    </div>

                    {/* Favicon Upload */}
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            {t('settings.favicon')}
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {faviconPreview ? (
                                <div className="relative">
                                    <img
                                        src={faviconPreview}
                                        alt="Favicon preview"
                                        className="max-h-16 mx-auto mb-4"
                                    />
                                    <button
                                        onClick={handleDeleteFavicon}
                                        className="text-red-600 hover:text-red-800 flex items-center justify-center gap-1 mx-auto"
                                    >
                                        <FiTrash2 /> {t('settings.deleteFavicon')}
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                                    <p className="text-gray-500 mb-2">{t('settings.noFavicon')}</p>
                                </div>
                            )}
                            <label className="cursor-pointer mt-4">
                                <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <FiUpload className="mr-2" />
                                    {t('settings.uploadFavicon')}
                                </span>
                                <input
                                    type="file"
                                    accept="image/png,image/x-icon,image/svg+xml"
                                    onChange={handleFaviconChange}
                                    className="hidden"
                                />
                            </label>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('settings.faviconRecommendation')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stamp Upload Section */}
            <div className="bg-white shadow rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Stamp / Seal
                </h2>

                <div className="max-w-md">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                        Official Stamp Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        {stampPreview ? (
                            <div className="relative">
                                <img
                                    src={stampPreview}
                                    alt="Stamp preview"
                                    className="max-h-32 mx-auto mb-4 rounded-full"
                                />
                                <button
                                    onClick={handleDeleteStamp}
                                    className="text-red-600 hover:text-red-800 flex items-center justify-center gap-1 mx-auto"
                                >
                                    <FiTrash2 /> Delete Stamp
                                </button>
                            </div>
                        ) : (
                            <div>
                                <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                                <p className="text-gray-500 mb-2">No stamp uploaded</p>
                            </div>
                        )}
                        <label className="cursor-pointer mt-4">
                            <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                <FiUpload className="mr-2" />
                                Upload Stamp
                            </span>
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleStampChange}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                        PNG or transparent image recommended. Max 2 MB.
                    </p>
                </div>
            </div>

            {/* Authority Signature Upload Section */}
            <div className="bg-white shadow rounded-xl p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    Authority Signature / अधिकाऱ्याची सही
                </h2>

                <div className="max-w-md">
                    <label className="block text-base font-medium text-gray-700 mb-2">
                        Authority Signature Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        {authoritySignaturePreview ? (
                            <div className="relative">
                                <img
                                    src={authoritySignaturePreview}
                                    alt="Authority signature preview"
                                    className="max-h-24 mx-auto mb-4"
                                />
                                <button
                                    onClick={handleDeleteAuthoritySignature}
                                    className="text-red-600 hover:text-red-800 flex items-center justify-center gap-1 mx-auto"
                                >
                                    <FiTrash2 /> Delete Signature
                                </button>
                            </div>
                        ) : (
                            <div>
                                <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                                <p className="text-gray-500 mb-2">No authority signature uploaded</p>
                            </div>
                        )}
                        <label className="cursor-pointer mt-4">
                            <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                <FiUpload className="mr-2" />
                                Upload Signature
                            </span>
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleAuthoritySignatureChange}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                        Upload the authority person's handwritten signature. PNG with transparent background recommended. Max 2 MB.
                    </p>
                </div>
            </div>

            {/* Office Details Section */}
            <div className="bg-white shadow rounded-xl p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    {t('settings.officeDetails')}
                </h2>

                <form onSubmit={handleOfficeSubmit} className="space-y-4">
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            {t('settings.officeName')} (English)
                        </label>
                        <input
                            type="text"
                            value={settings.office_name || ''}
                            onChange={(e) => setSettings(prev => ({ ...prev, office_name: e.target.value }))}
                            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g. Marriage Registration Office"
                        />
                    </div>
                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            Office Name (मराठी / Marathi)
                        </label>
                        <input
                            type="text"
                            value={settings.office_name_mr || ''}
                            onChange={(e) => setSettings(prev => ({ ...prev, office_name_mr: e.target.value }))}
                            className="w-full px-4 py-3 text-lg border bg-orange-50 border-orange-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="उदा. विवाह नोंदणी कार्यालय"
                        />
                    </div>

                    

                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            {t('settings.officeAddress')} (English)
                        </label>
                        <textarea
                            value={settings.office_address || ''}
                            onChange={(e) => setSettings(prev => ({ ...prev, office_address: e.target.value }))}
                            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                            placeholder="Full address in English"
                        />
                    </div>

                    <div>
                        <label className="block text-base font-medium text-gray-700 mb-2">
                            {t('settings.officeAddress')} (मराठी / Marathi)
                        </label>
                        <textarea
                            value={settings.office_address_mr || ''}
                            onChange={(e) => setSettings(prev => ({ ...prev, office_address_mr: e.target.value }))}
                            className="w-full px-4 py-3 text-lg border border-orange-300 bg-orange-50 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            rows={3}
                            placeholder="संपूर्ण पत्ता मराठीत"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                {t('settings.phone')}
                            </label>
                            <input
                                type="text"
                                value={settings.office_phone || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, office_phone: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="+91..."
                            />
                        </div>
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                {t('settings.email')}
                            </label>
                            <input
                                type="email"
                                value={settings.office_email || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, office_email: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="office@example.com"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                Authority Name (English)
                            </label>
                            <input
                                type="text"
                                value={settings.authority_name || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, authority_name: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g. Mr. Ram Patil - President"
                            />
                        </div>
                        {/* <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                Authority Name (मराठी / Marathi)
                            </label>
                            <input
                                type="text"
                                value={settings.authority_name_mr || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, authority_name_mr: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-orange-300 bg-orange-50 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="उदा. श्री. राम पाटील - अध्यक्ष"
                            />
                        </div> */}
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                Place (English)
                            </label>
                            <input
                                type="text"
                                value={settings.place || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, place: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g. Nashik"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-base font-medium text-gray-700 mb-2">
                                Place (मराठी / Marathi)
                            </label>
                            <input
                                type="text"
                                value={settings.place_mr || ''}
                                onChange={(e) => setSettings(prev => ({ ...prev, place_mr: e.target.value }))}
                                className="w-full px-4 py-3 text-lg border border-orange-300 bg-orange-50 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="उदा. नाशिक"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                            <FiSave className="mr-2" />
                            {loading ? t('settings.saving') : t('settings.save')}
                        </button>
                    </div>
                </form>
            </div>

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={t('common.areYouSure')}
                message={
                    confirmDialog.type === 'logo'
                        ? t('settings.deleteLogoConfirm')
                        : confirmDialog.type === 'favicon'
                        ? t('settings.deleteFaviconConfirm')
                        : confirmDialog.type === 'stamp'
                        ? 'Are you sure you want to delete the stamp?'
                        : 'Are you sure you want to delete the authority signature?'
                }
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmDialog({ isOpen: false, type: '' })}
            />
        </div>
    );
};

export default Settings;